import { createBrowserRouter, Navigate, Outlet, RouterProvider, useRouteError, useMatch, To } from 'react-router-dom';
import { Flex } from '@atoms/layout';
import { ErrorMessage } from '@organisms/dataDisplay';
import { ComingSoonPage } from '@organisms/dataDisplay';
import { MainLayout } from '@templates/mainLayout';
import { ChatwootWrapper } from '@shiftnex/framework/src/components/molecules/supportChat';
import { Component as Dashboard } from '@pages/dashboard';
import {
  SettingsRoot,
  QualificationsSettings,
  LinesOfBusinessSettings,
  AgencyTypesSettings,
  FacilityTypesSettings,
} from '@pages/settings';
import { Component as ClientsPage } from '@pages/clients';
import { AuthGuard } from '@molecules/auth/authGuard';
import { AnalyticsPageLayout } from '@pages/analytics/pageLayout';

export function Routing() {
  return <RouterProvider router={router} />;
}

type RedirectProps = {
  match: string;
  to: To;
};
function Redirect({ match, to }: RedirectProps) {
  const hasMatch = useMatch(match);

  if (hasMatch) {
    // Redirect to default sub page
    return <Navigate to={to} replace={true} />;
  }
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthGuard>
        <ChatwootWrapper>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </ChatwootWrapper>
      </AuthGuard>
    ),
    // TODO: make 404 page
    ErrorBoundary: (...rest) => {
      const error = useRouteError() as Error;
      // yes - this ^ method doesn't return a formatter error with normal adequate error codes.
      // So we have to check the message
      if(error && error.message && error.message.includes('Failed to fetch dynamically imported module')) {
        window.location.reload(); // reload page in order to attempt to get a new one build
        return;
      }
      console.error('ERROR', error, );
      console.info('ERROR', { err: error }, ...rest);
      return (
        <>
          <MainLayout>
            <ErrorMessage error={error} />
          </MainLayout>
        </>
      );
    },
    children: [
      {
        path: '/',
        element: <Redirect match="/" to="/dashboard" />,
      },
      {
        path: '/dashboard',
        element: <Dashboard />,
      },
      {
        path: '/clients',
        element: <Redirect match="/clients" to="/clients/requests" />,
        children: [
          {
            path: '/clients/requests',
            element: <ClientsPage subpage="requests" />,
          },
          {
            path: '/clients/agencies',
            element: <ClientsPage subpage="agencies" />,
          },
          {
            path: '/clients/facilities',
            element: <ClientsPage subpage="facilities" />,
          },
        ],
      },
      {
        path: '/analytics',
        element: <Redirect match="/analytics" to="/analytics/apps-activity" />,
        children: [
          {
            path: '/analytics/:id',
            element: <AnalyticsPageLayout />,
          },
        ],
      },
      {
        path: '/settings',
        element: <SettingsRoot />,
        children: [
          {
            path: '/settings/organization',
            element: <Redirect match="/settings/organization" to="/settings/organization/values" />,
          },
          {
            path: '/settings/organization/values',
            element: (
              <Redirect match="/settings/organization/values" to="/settings/organization/values/qualifications" />
            ),
            children: [
              {
                path: '/settings/organization/values/qualifications',
                element: <QualificationsSettings />,
              },
              {
                path: '/settings/organization/values/lines-of-business',
                element: <LinesOfBusinessSettings />,
              },
              {
                path: '/settings/organization/values/agency-types',
                element: <AgencyTypesSettings />,
              },
              {
                path: '/settings/organization/values/facility-types',
                element: <FacilityTypesSettings />,
              },
            ],
          },
          {
            path: '/settings/*',
            element: (
              <Flex justifyCenter alignCenter height="100%" width="100%">
                <ComingSoonPage />
              </Flex>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/*',
    element: (
      <AuthGuard>
        <MainLayout>
          <ComingSoonPage />
        </MainLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/404',
    element: <ComingSoonPage />,
  },
]);
